import { useEffect } from 'react';

/**
 * Updates the page title, meta description, keywords, and canonical URL for SEO and AI bots.
 */
export const updateMetadata = (title: string, description: string, keywords?: string, canonicalUrl?: string, image?: string) => {
  document.title = title;
  
  // Update description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = description;
    document.head.appendChild(meta);
  }

  // Update keywords
  if (keywords) {
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = keywords;
      document.head.appendChild(meta);
    }
  }
  
  // Update canonical
  const canonical = document.querySelector('link[rel="canonical"]');
  
  // Clean up and normalize pathname to prevent trailing slash duplicates
  let cleanPath = window.location.pathname;
  if (cleanPath.length > 1 && cleanPath.endsWith('/')) {
    cleanPath = cleanPath.slice(0, -1);
  }
  
  // Format target canonical strictly using the preferred domain
  let targetCanonical = canonicalUrl || 'https://lizamoermancoach.nl' + cleanPath;
  
  // Standardize the host to the preferred canonical domain
  if (targetCanonical.startsWith('http://') || targetCanonical.startsWith('https://')) {
    targetCanonical = targetCanonical.replace(/^(https?:\/\/)(localhost(:\d+)?|[^/]+)/, 'https://lizamoermancoach.nl');
  }

  // Ensure overall canonical structure doesn't end with a trailing slash unless it's the home root
  try {
    const parsedUrl = new URL(targetCanonical);
    if (parsedUrl.pathname.length > 1 && parsedUrl.pathname.endsWith('/')) {
      parsedUrl.pathname = parsedUrl.pathname.slice(0, -1);
    }
    targetCanonical = parsedUrl.toString();
  } catch (e) {
    if (targetCanonical.length > 1 && targetCanonical.endsWith('/')) {
      targetCanonical = targetCanonical.slice(0, -1);
    }
  }

  if (canonical) {
    canonical.setAttribute('href', targetCanonical);
  } else {
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = targetCanonical;
    document.head.appendChild(link);
  }
  
  // Also update Open Graph for social sharing
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', title);
  
  const ogDesc = document.querySelector('meta[property="og:description"]');
  if (ogDesc) ogDesc.setAttribute('content', description);

  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.setAttribute('content', targetCanonical);

  // Update dynamic og:image and twitter:image
  if (image) {
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', image);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image');
      meta.setAttribute('content', image);
      document.head.appendChild(meta);
    }

    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', image);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'twitter:image');
      meta.setAttribute('content', image);
      document.head.appendChild(meta);
    }
  }
};

/**
 * Hook to update metadata on component mount.
 */
export const useMetadata = (
  title: string,
  description: string,
  keywords?: string,
  canonicalUrl?: string,
  enabled = true,
  image?: string
) => {
  useEffect(() => {
    if (enabled) {
      updateMetadata(title, description, keywords, canonicalUrl, image);
    }
  }, [title, description, keywords, canonicalUrl, enabled, image]);
};
